import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "mc_admin_token";

export const api = axios.create({
    baseURL: BACKEND_URL,
    headers: { "Content-Type": "application/json" },
});

// Attach Authorization header automatically
api.interceptors.request.use((config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

// Public
export const fetchArticles = async () => (await api.get("/api/articles")).data;
export const fetchArticle = async (slug) => (await api.get(`/api/articles/${slug}`)).data;
export const submitContact = async (payload) => (await api.post("/api/contact", payload)).data;

// Auth
export const adminLogin = async (email, password) => {
    const { data } = await api.post("/api/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    return data;
};
export const adminLogout = async () => {
    try { await api.post("/api/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
};
export const adminMe = async () => (await api.get("/api/auth/me")).data;
export const getStoredToken = () => localStorage.getItem(TOKEN_KEY);
export const clearStoredToken = () => localStorage.removeItem(TOKEN_KEY);

// Admin
export const adminListArticles = async () => (await api.get("/api/admin/articles")).data;
export const adminGetArticle = async (id) => (await api.get(`/api/admin/articles/${id}`)).data;
export const adminUpdateArticle = async (id, payload) => (await api.patch(`/api/admin/articles/${id}`, payload)).data;
export const adminPublishArticle = async (id) => (await api.post(`/api/admin/articles/${id}/publish`)).data;
export const adminUnpublishArticle = async (id) => (await api.post(`/api/admin/articles/${id}/unpublish`)).data;
export const adminDeleteArticle = async (id) => (await api.delete(`/api/admin/articles/${id}`)).data;
export const adminGenerateArticle = async () => (await api.post("/api/admin/articles/generate")).data;
export const adminListContacts = async () => (await api.get("/api/admin/contacts")).data;
export const adminUnreadCount = async () => (await api.get("/api/admin/contacts/unread_count")).data;
export const adminMarkContactRead = async (id) => (await api.post(`/api/admin/contacts/${id}/read`)).data;
export const adminMarkContactUnread = async (id) => (await api.post(`/api/admin/contacts/${id}/unread`)).data;
export const adminMarkAllContactsRead = async () => (await api.post("/api/admin/contacts/mark_all_read")).data;
export const adminGenerateLegalAssessment = async (id, force = false) =>
    (await api.post(`/api/admin/contacts/${id}/legal-assessment${force ? "?force=true" : ""}`)).data;
export const adminDeleteLegalAssessment = async (id) =>
    (await api.delete(`/api/admin/contacts/${id}/legal-assessment`)).data;
export const adminStats = async () => (await api.get("/api/admin/stats")).data;

// ========== Legal HR Module ==========
export const hrCcnlPresets = async () => (await api.get("/api/admin/hr/ccnl-presets")).data;
export const hrPortfolioSummary = async () => (await api.get("/api/admin/hr/portfolio-summary")).data;
// Clients
export const hrListClients = async () => (await api.get("/api/admin/hr/clients")).data;
export const hrCreateClient = async (payload) => (await api.post("/api/admin/hr/clients", payload)).data;
export const hrGetClient = async (id) => (await api.get(`/api/admin/hr/clients/${id}`)).data;
export const hrUpdateClient = async (id, payload) => (await api.patch(`/api/admin/hr/clients/${id}`, payload)).data;
export const hrDeleteClient = async (id) => (await api.delete(`/api/admin/hr/clients/${id}`)).data;
export const hrClientDashboard = async (id) => (await api.get(`/api/admin/hr/clients/${id}/dashboard`)).data;
// Employees
export const hrListEmployees = async (clientId) => (await api.get(`/api/admin/hr/clients/${clientId}/employees`)).data;
export const hrCreateEmployee = async (payload) => (await api.post("/api/admin/hr/employees", payload)).data;
export const hrUpdateEmployee = async (id, payload) => (await api.patch(`/api/admin/hr/employees/${id}`, payload)).data;
export const hrDeleteEmployee = async (id) => (await api.delete(`/api/admin/hr/employees/${id}`)).data;
// Disputes
export const hrListDisputes = async (clientId) => (await api.get(`/api/admin/hr/clients/${clientId}/disputes`)).data;
export const hrCreateDispute = async (payload) => (await api.post("/api/admin/hr/disputes", payload)).data;
export const hrUpdateDispute = async (id, payload) => (await api.patch(`/api/admin/hr/disputes/${id}`, payload)).data;
export const hrDeleteDispute = async (id) => (await api.delete(`/api/admin/hr/disputes/${id}`)).data;
// Disciplinary
export const hrListDisciplinary = async (clientId) => (await api.get(`/api/admin/hr/clients/${clientId}/disciplinary`)).data;
export const hrCreateDisciplinary = async (payload) => (await api.post("/api/admin/hr/disciplinary", payload)).data;
export const hrUpdateDisciplinary = async (id, payload) => (await api.patch(`/api/admin/hr/disciplinary/${id}`, payload)).data;
export const hrDeleteDisciplinary = async (id) => (await api.delete(`/api/admin/hr/disciplinary/${id}`)).data;
// Import / Export
export const hrImportPreview = async (clientId, file) => {
    const form = new FormData();
    form.append("client_id", clientId);
    form.append("file", file);
    const { data } = await api.post("/api/admin/hr/import/preview", form, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
};
export const hrImportConfirm = async (clientId, employees) => (await api.post("/api/admin/hr/import/confirm", { client_id: clientId, employees })).data;
export const hrImportTemplateUrl = () => `${BACKEND_URL}/api/admin/hr/import/template.csv`;
export const hrAuditReportUrl = (clientId) => `${BACKEND_URL}/api/admin/hr/clients/${clientId}/audit-report.pdf`;
export const hrDownloadAuditReport = async (clientId) => {
    const res = await api.get(`/api/admin/hr/clients/${clientId}/audit-report.pdf`, { responseType: "blob" });
    return res.data;
};
export const hrDeadlines = async (fromDate, toDate) => {
    const params = {};
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    return (await api.get("/api/admin/hr/deadlines", { params })).data;
};
export const hrAuditHistory = async () => (await api.get("/api/admin/hr/audit-reports/history")).data;
export const hrDownloadPortfolioReport = async () => {
    const res = await api.get("/api/admin/hr/portfolio-report.pdf", { responseType: "blob" });
    return res.data;
};

// Utility for error messages
export const formatApiError = (err, fallback = "Si è verificato un errore.") => {
    const detail = err?.response?.data?.detail;
    if (!detail) return err?.message || fallback;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" — ");
    return String(detail);
};
